/**
 * 登录页（07 开发文档 /login + 决策 8 首登强制改密）。
 * 登录成功 → mustChangePassword=true 强制改密（改密后重登）→ 进工作台。
 */
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { Button, FormField } from '@chronospeak/ui';
import { ApiError } from '../lib/api';
import { useSession } from '../lib/session';
import { adminHttp } from '../lib/api';

export function LoginPage() {
  const { login } = useSession();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const admin = await login(email.trim(), password);
      if (admin.mustChangePassword) {
        navigate('/change-password', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : '登录失败，请稍后再试。'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className='login-page'>
      <form className='login-card' onSubmit={onSubmit} noValidate>
        <h1 className='login-card__title'>Chronuspeak 运营后台</h1>
        <p className='login-card__sub'>运营管理员登录</p>
        <FormField
          label='邮箱'
          type='email'
          name='email'
          autoComplete='username'
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoFocus
        />
        <FormField
          label='密码'
          type='password'
          name='password'
          autoComplete='current-password'
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        {error && (
          <p className='login-card__error' role='alert'>
            <span aria-hidden='true'>✎ </span>
            {error}
          </p>
        )}
        <Button type='submit' size='lg' disabled={busy} style={{ width: '100%' }}>
          {busy ? '登录中…' : '登录'}
        </Button>
      </form>
    </main>
  );
}

/** 首登强制改密页（决策 8）：改密成功后全部会话失效 → 回登录页重登 */
export function ChangePasswordPage() {
  const { admin, refreshAdmin } = useSession();
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (next !== confirm) {
      setError('两次输入的新密码不一致。');
      return;
    }
    if (next.length < 10) {
      setError('新密码至少 10 位。');
      return;
    }
    setBusy(true);
    try {
      await adminHttp.post('/admin/auth/change-password', {
        currentPassword: current,
        newPassword: next,
      });
      // 改密成功后回到登录页重新认证（服务端已 revoke 全部会话）
      navigate('/login', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '修改失败，请稍后再试。');
    } finally {
      setBusy(false);
      void refreshAdmin;
    }
  };

  return (
    <main className='login-page'>
      <form className='login-card' onSubmit={onSubmit} noValidate>
        <h1 className='login-card__title'>修改初始密码</h1>
        <p className='login-card__sub'>
          首次登录需设置新密码（{admin?.email ?? ''}）
        </p>
        <FormField
          label='当前密码'
          type='password'
          autoComplete='current-password'
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          required
        />
        <FormField
          label='新密码（至少 10 位）'
          type='password'
          autoComplete='new-password'
          value={next}
          onChange={(e) => setNext(e.target.value)}
          required
          minLength={10}
        />
        <FormField
          label='确认新密码'
          type='password'
          autoComplete='new-password'
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          required
        />
        {error && (
          <p className='login-card__error' role='alert'>
            <span aria-hidden='true'>✎ </span>
            {error}
          </p>
        )}
        <Button type='submit' size='lg' disabled={busy} style={{ width: '100%' }}>
          {busy ? '提交中…' : '设置新密码'}
        </Button>
      </form>
    </main>
  );
}
